import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CoreConstants } from "../../../core/models/constants/core.constants";
import DynamicForm from "../../../core/presentations/DynamicForm";
import Validators from "../../../ui/helpers/validators";
import TopLabelFieldTemplate from "../../../ui/templates/fields/TopLabelFieldTemplate";
import VerticalFormTemplate from "../../../ui/templates/objects/VerticalFormTemplate";
import PrwcBasicButton from "../../../ui/widgets/custom/buttons/PrwcBasicButton";
import PrwcOutlinedButton from "../../../ui/widgets/custom/buttons/PrwcOutlinedButton";
import { OperationType } from "../../models/enums/home-generic.enums";

const ProfilePrimaryContact = (props: any) => {
  const { mode, data, onSave, onCancel } = props;
  const { t } = useTranslation();

  const [formData, setFormData] = useState(null);

  const onChange = (formData: any) => {
    setFormData(formData);
  };

  const onSubmit = (data: any) => {
    onSave(formData);
  };

  const handleOnCancel = () => {
    setFormData(null);
    onCancel();
  };

  const onError = (data: any) => {};

  const formButtons = () => {
    return (
      <div className="flex w-full">
        <div className="mr-2">
          <PrwcBasicButton
            type="submit"
            labelKey="generic.action.update"
            className="w-24 bg-blue-700 p-button-sm"
          ></PrwcBasicButton>
        </div>
        <div>
          <PrwcOutlinedButton
            onClick={handleOnCancel}
            type="reset"
            labelKey="generic.action.cancel"
            className="w-24 p-button-outlined p-button-sm"
          ></PrwcOutlinedButton>
        </div>
      </div>
    );
  };

  const schema: any = {
    type: "object",
    properties: {
      primaryPhoneNumber: {
        type: "string",
        title: t("profileSummary.label.phone"),
      },
      primaryEmailId: {
        type: "string",
        title: t("profileSummary.label.emailId"),
      },
      address: {
        type: "string",
        title: t("profileSummary.label.address"),
      },
      stateOrProvince: {
        type: "string",
        title: t("profileSummary.label.stateOrProvince"),
      },
      country: {
        type: "string",
        title: t("profileSummary.label.country"),
      },
      zipCode: {
        type: "number",
        title: t("profileSummary.label.zipCode"),
      },
    },
    required: ["primaryEmailId"],
  };

  const validationSchema: any = {
    primaryPhoneNumber: {
      validators: [
        {
          validate: (value: string) => {
            return Validators.isValidPhoneNumber(value);
          },
          messageKey: "profileSummary.validation.phoneNumberInvalid",
        },
      ],
    },
    primaryEmailId: {
      validators: [
        {
          validate: (value: string) => {
            return !Validators.isStringEmpty(value);
          },
          messageKey: "profileSummary.validation.emailRequired",
        },
        {
          validate: (value: string) => {
            return Validators.isRegexPatternValid(
              CoreConstants.REGEX_EMAIL,
              value
            );
          },
          messageKey: "profileSummary.validation.emailInvalid",
        },
      ],
    },
  };

  const uiSchema: any = {
    "ui:ObjectFieldTemplate": VerticalFormTemplate,
    primaryPhoneNumber: {
      "ui:widget": "prwPhoneNumber",
      "ui:FieldTemplate": TopLabelFieldTemplate,
      classNames: "p-col-12",
      "ui:emptyValue": "",
    },
    primaryEmailId: {
      "ui:widget": "prwInputText",
      "ui:FieldTemplate": TopLabelFieldTemplate,
      classNames: "p-col-12",
      "ui:emptyValue": "",
    },
    address: {
      "ui:widget": "prwInputTextArea",
      "ui:FieldTemplate": TopLabelFieldTemplate,
      classNames: "p-col-12",
      "ui:emptyValue": "",
    },
    stateOrProvince: {
      "ui:widget": "prwInputText",
      "ui:FieldTemplate": TopLabelFieldTemplate,
      classNames: "p-col-12",
      "ui:emptyValue": "",
    },
    country: {
      "ui:widget": "prwCountryDropdown",
      "ui:FieldTemplate": TopLabelFieldTemplate,
      classNames: "p-col-12",
      "ui:emptyValue": "",
    },
    zipCode: {
      "ui:widget": "prwInputText",
      "ui:FieldTemplate": TopLabelFieldTemplate,
      classNames: "p-col-12",
      "ui:emptyValue": "",
    },
  };

  return (
    <div>
      {mode === OperationType.READ ? (
        <div className="text-sm">
          <div className="my-1">
            <span className="font-semibold text-gray-700">
              {t("profileSummary.label.phone")}
            </span>
            <span className="mx-1">-</span>
            <span>{data?.phone}</span>
          </div>
          <div className="my-1">
            <span className="font-semibold text-gray-700">
              {t("profileSummary.label.emailId")}
            </span>
            <span className="mx-1">-</span>
            <span>{data?.emailId}</span>
          </div>
          <div className="my-1">
            <span className="font-semibold text-gray-700">
              {t("profileSummary.label.address")}
            </span>
            <span className="mx-1">-</span>
            <span>{data?.address}</span>
          </div>
          <div className="my-1">
            <span className="font-semibold text-gray-700">
              {t("profileSummary.label.stateOrProvince")}
            </span>
            <span className="mx-1">-</span>
            <span>{data?.state}</span>
          </div>
          <div className="my-1">
            <span className="font-semibold text-gray-700">
              {t("profileSummary.label.country")}
            </span>
            <span className="mx-1">-</span>
            <span>{data?.country}</span>
          </div>
          <div className="my-1">
            <span className="font-semibold text-gray-700">
              {t("profileSummary.label.zipCode")}
            </span>
            <span className="mx-1">-</span>
            <span>{data?.zipCode}</span>
          </div>
        </div>
      ) : (
        <div>
          <DynamicForm
            onChange={onChange}
            formData={formData}
            onSubmit={onSubmit}
            onError={onError}
            schema={schema}
            uiSchema={uiSchema}
            validationSchema={validationSchema}
            formButtons={() => formButtons()}
          ></DynamicForm>
        </div>
      )}
    </div>
  );
};

export default ProfilePrimaryContact;
